<template>
  <section class="online-meeting" aria-label="Microsoft Teams Meeting">
    <div class="online-meeting__heading">
      <span class="online-meeting__icon"><v-icon color="white">mdi-microsoft-teams</v-icon></span>
      <div><span class="online-meeting__eyebrow">MICROSOFT TEAMS · 1:1</span><h3>Online zusammenkommen</h3></div>
    </div>
    <p class="online-meeting__status" role="status">{{ statusText }}</p>
    <div class="online-meeting__actions">
      <a v-if="joinUrl" :href="joinUrl" target="_blank" rel="noopener noreferrer" class="online-meeting__join">
        <v-icon small color="white">mdi-video-outline</v-icon> In Teams teilnehmen <v-icon small color="white">mdi-open-in-new</v-icon>
      </a>
      <button v-if="joinUrl" type="button" class="online-meeting__secondary" @click="copyLink">{{ copied ? "Link kopiert" : "Link kopieren" }}</button>
      <button v-if="canRetry" type="button" :disabled="loading" class="online-meeting__join" @click="load(true)">
        <v-icon small color="white">mdi-refresh</v-icon> {{ loading ? "Teams wird aktualisiert …" : inactive ? "Absage in Teams wiederholen" : "Teams erneut vorbereiten" }}
      </button>
      <button v-if="!joinUrl && !denied && !inactive && !canRetry" type="button" :disabled="loading" class="online-meeting__secondary" @click="load(false)">
        {{ loading ? "Link wird geladen …" : "Status aktualisieren" }}
      </button>
    </div>
    <small v-if="joinUrl" class="online-meeting__hint">Ihr nutzt beide diesen Link. Teams im Browser oder in der App öffnen.</small>
  </section>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"

export default {
  name: "OnlineMeetingCard",
  props: { appointment: { type: Object, required: true } },
  data: () => ({ meeting: null, loading: false, denied: false, loadError: false, copied: false, requestVersion: 0 }),
  computed: {
    appointmentId() { return this.appointment._id || this.appointment.id },
    refreshKey() { return [this.appointmentId, this.appointment.updatedAt || this.appointment.sourceUpdatedAt, this.appointment.status, this.appointment.deleted].join(":") },
    inactive() { return this.appointment.deleted || ["cancelled", "noshow"].includes(this.appointment.status) || this.meeting?.status === "cancelled" },
    joinUrl() {
      if (this.inactive || this.meeting?.status !== "ready") return null
      try {
        const url = new URL(this.meeting.joinUrl)
        return url.protocol === "https:" && !url.username && !url.password && ["teams.microsoft.com", "teams.live.com", "teams.cloud.microsoft"].includes(url.hostname) ? url.href : null
      } catch (_) { return null }
    },
    canRetry() { return !this.denied && userService.hasRight("stat_admin") && !this.joinUrl && (!this.inactive || this.meeting?.needsSync) },
    statusText() {
      if (this.denied) return "Der Teams-Link steht den beiden Beteiligten zur Verfügung."
      if (this.inactive) return this.meeting?.needsSync ? "Der Termin ist abgesagt. Die Teams-Sitzung muss noch entfernt werden." : "Dieser Termin wurde abgesagt. Der Beitritt ist nicht mehr verfügbar."
      if (this.loading) return "Dein persönlicher Meeting-Link wird geladen …"
      if (this.joinUrl) return "Alles bereit für euer Gespräch. Mit einem Klick seid ihr verbunden."
      if (this.loadError) return "Der Link konnte gerade nicht geladen werden. Bitte erneut versuchen."
      if (this.meeting?.status === "failed") return "Der Termin ist gespeichert. Der Teams-Link muss noch einmal vorbereitet werden."
      return "Der Termin ist gespeichert. Euer gemeinsamer Teams-Link wird vorbereitet."
    },
  },
  watch: { refreshKey: { immediate: true, handler() { this.meeting = null; this.copied = false; this.load(false) } } },
  beforeDestroy() { this.requestVersion++ },
  methods: {
    async load(retry) {
      if (!this.appointmentId) return
      const version = ++this.requestVersion
      this.loading = true
      this.denied = false
      this.loadError = false
      try {
        const result = retry ? await api.shiftManagement.retryOnlineMeeting(this.appointmentId) : await api.shiftManagement.fetchOnlineMeeting(this.appointmentId)
        if (version === this.requestVersion) this.meeting = result
      } catch (error) {
        if (version !== this.requestVersion) return
        this.meeting = null
        this.denied = [401, 403].includes(error?.response?.status)
        this.loadError = !this.denied
      } finally { if (version === this.requestVersion) this.loading = false }
    },
    async copyLink() {
      if (!this.joinUrl) return
      try { await navigator.clipboard.writeText(this.joinUrl); this.copied = true } catch (_) {
        this.$toast.info("Über «In Teams teilnehmen» kannst du den Link direkt öffnen.")
      }
    },
  },
}
</script>

<style scoped>
.online-meeting { box-sizing: border-box; width: 100%; margin: 12px 0; padding: 22px; color: #fff; background: radial-gradient(ellipse at 100% 0, rgba(139, 132, 239, .24), transparent 65%), linear-gradient(135deg, #252643, #191c30); border: 1px solid rgba(174, 170, 255, .3); border-radius: 20px; }
.online-meeting__heading { display: flex; align-items: center; gap: 12px; }
.online-meeting__icon { display: grid; place-items: center; flex: 0 0 44px; height: 44px; border-radius: 14px; background: #6264a7; }
.online-meeting__eyebrow { color: #c3c2f7; font-size: 10px; font-weight: 700; letter-spacing: 1.1px; }
.online-meeting h3 { margin: 4px 0 0; color: #fff; font-size: 18px; line-height: 1.3; }
.online-meeting__status { margin: 15px 0; color: #dfdeef; font-size: 14px; line-height: 1.5; }
.online-meeting__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.online-meeting__join, .online-meeting__secondary { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 44px; padding: 10px 14px; border-radius: 12px; font: inherit; font-size: 13px; font-weight: 700; text-decoration: none; cursor: pointer; }
.online-meeting__join { color: #fff; background: #575ab0; border: 1px solid #9495e0; }
.online-meeting__secondary { color: #e7e6ff; background: transparent; border: 1px solid rgba(174, 170, 255, .35); }
.online-meeting__join:hover, .online-meeting__secondary:hover { background: #474a91; }
.online-meeting button:disabled { opacity: .65; cursor: wait; }
.online-meeting a:focus-visible, .online-meeting button:focus-visible { outline: 3px solid #d0ceff; outline-offset: 3px; }
.online-meeting__hint { display: block; margin-top: 13px; color: #bfbed7; font-size: 11px; line-height: 1.5; }
@media (max-width: 440px) { .online-meeting { padding: 17px; } .online-meeting__join { width: 100%; } }
</style>
